// No imports: dd-trace's tracing setup reads this before `tracer.init()`, and
// anything it pulls in (pino above all) would load uninstrumented.

/**
 * The Datadog Error Tracking `error.fingerprint` for an error, or `undefined`
 * to keep Datadog's default grouping (type, message words, top frame).
 *
 * Opt-in only, and never built from message text:
 * - an error that carries its own string `fingerprint` (`VendorApiError`
 *   keys it on the vendor's error code) keeps it;
 * - a Prisma known request error is keyed on its `P` code and model, so one
 *   failure mode is one issue however the message wording drifts;
 * - everything else gets `undefined`.
 *
 * Shared by the log serializer and the web app's span hook so the log and the
 * span for one failure carry the same key.
 */
export const getErrorFingerprint = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const { fingerprint } = error as { fingerprint?: unknown };
  if (typeof fingerprint === "string" && fingerprint) {
    return fingerprint;
  }

  return _getPrismaFingerprint(error);
};

/**
 * Duck-typed rather than `instanceof`, so this file never loads
 * `@prisma/client`.
 */
const _getPrismaFingerprint = (error: object): string | undefined => {
  const { name, code, meta } = error as {
    name?: unknown;
    code?: unknown;
    meta?: { modelName?: unknown };
  };
  if (
    name !== "PrismaClientKnownRequestError" ||
    typeof code !== "string" ||
    !_PRISMA_ERROR_CODE.test(code)
  ) {
    return undefined;
  }

  const modelName = meta?.modelName;
  return typeof modelName === "string" && _MODEL_NAME.test(modelName)
    ? `prisma:${code}:${modelName}`
    : `prisma:${code}`;
};

const _PRISMA_ERROR_CODE = /^P\d{4}$/;
const _MODEL_NAME = /^[A-Za-z0-9_]{1,64}$/;
